import {
  startSubscriptionWithTrialPlan,
  validateAvailableTrialPlan,
} from '@modusign/openapi-client-subscription';
import { all, call, put, retry, select, takeLatest, takeLeading } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { PLAN_TRIAL } from '@app/features/subscription/constants';
import {
  startTrialPlanFailure,
  startTrialPlanSuccess,
  startTrialWithSurvey,
  startTrialWithSurveyFailure,
  startTrialWithSurveySuccess,
  validateTrialPlanFailure,
  validateTrialPlanSuccess,
} from '@app/features/subscription/state';
import workspaceService from '@app/libs/workspaceService';
import {
  SUBSCRIPTION_TRIAL_PLAN_START_REQUEST,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST,
  SUBSCRIPTION_TRIAL_PLAN_VALIDATE_REQUEST,
} from '@app/store/actionTypes';
import { recordGTMEvent } from '@app/store/analytics/actions';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import { putSurvey } from '@app/store/transport-layer/identityaccess/survey';
import { getType } from '@app/store/user/user/selectors';

export function* startTrialPlanSaga() {
  const userType: ReturnType<typeof getType> = yield select(getType);

  try {
    yield call(startSubscriptionWithTrialPlan, workspaceService.getWorkspaceId(), {
      planId: PLAN_TRIAL,
    });
    yield put(startTrialPlanSuccess());
    yield put(modalClose());
    yield put(recordGTMEvent('getTrialPlan', { userType }));
  } catch (error) {
    yield put(startTrialPlanFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export function* startTrialPlanWithSurveySaga({
  payload: {
    companyCategory,
    department,
    expectedStartTime,
    expectedUsage,
    companyName,
    companyScale,
    userType,
    phoneNumber,
  },
}: ReturnType<typeof startTrialWithSurvey>) {
  try {
    let survey: {
      companyCategory?: string;
      companyName?: string;
      companyScale?: string;
      department?: string;
      expectedStartTime?: string;
      expectedUsage?: string;
      phoneNumber: string;
      userType: string;
    } = { userType, phoneNumber };

    if (userType !== '개인이용자') {
      survey = {
        ...survey,
        companyCategory,
        companyName,
        companyScale,
        department,
        expectedStartTime,
        expectedUsage,
      };
    }
    yield call(putSurvey, survey);
    yield retry(3, 2 * 1000, startSubscriptionWithTrialPlan, workspaceService.getWorkspaceId(), {
      planId: PLAN_TRIAL,
    });
    yield put(recordGTMEvent('getTrialPlan', { userType }));
    yield put(startTrialWithSurveySuccess(survey));
    yield put(modalClose());
  } catch (error) {
    const SURVEY_UPDATE_RESPONSE: { [key: string]: string } = {
      'user not found': '존재하지 않는 유저입니다.',
    };
    yield put(
      modalOpen('CONFIRM', {
        content:
          SURVEY_UPDATE_RESPONSE[error.message] ||
          GLOBAL_ERROR_MESSAGE[error.message] ||
          error.message,
      })
    );
    yield put(startTrialWithSurveyFailure(error));
  }
}

export function* validateTrialPlanSaga() {
  try {
    const result: { isAvailableTrialPlan: boolean } = yield call(
      validateAvailableTrialPlan,
      workspaceService.getWorkspaceId()
    );
    yield put(validateTrialPlanSuccess(result));
  } catch (error) {
    yield put(validateTrialPlanFailure(error));
  }
}

export default function* sagas() {
  yield all([
    takeLeading(SUBSCRIPTION_TRIAL_PLAN_START_REQUEST, startTrialPlanSaga),
    takeLeading(SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST, startTrialPlanWithSurveySaga),
    takeLatest(SUBSCRIPTION_TRIAL_PLAN_VALIDATE_REQUEST, validateTrialPlanSaga),
  ]);
}
